import React, { useState, useEffect } from 'react';
import api from '../services/api';

const PreSaleCalculator = ({ isOpen, onClose }) => {
  const [taxRates, setTaxRates] = useState({});
  const [loading, setLoading] = useState(true);

  const [salePrice, setSalePrice] = useState('');
  const [county, setCounty] = useState('Lucas');
  const [isOutOfState, setIsOutOfState] = useState(false);
  const [tradeIn, setTradeIn] = useState('');
  const [docFee, setDocFee] = useState(300);
  const [fees, setFees] = useState([
    { id: 1, name: 'Temp Tag', amount: 20 },
    { id: 2, name: 'Title Fee', amount: 22.50 },
    { id: 3, name: 'Lien Fee', amount: 0 },
  ]);

  useEffect(() => {
    if (!isOpen) return;
    api.getTaxRates().then(data => {
      setTaxRates(data.rates || data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [isOpen]);

  const addFee = () => {
    setFees(prev => [...prev, { id: Date.now(), name: '', amount: 0 }]);
  };
  const removeFee = (id) => {
    setFees(prev => prev.filter(f => f.id !== id));
  };
  const updateFee = (id, field, value) => {
    setFees(prev => prev.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const price = parseFloat(salePrice) || 0;
  const trade = parseFloat(tradeIn) || 0;
  const doc = parseFloat(docFee) || 0;
  const taxableAmount = Math.max(0, price - trade);
  const taxRate = isOutOfState ? 0 : (taxRates[county] || 7.75);
  const salesTax = taxableAmount * taxRate / 100;
  const totalFees = fees.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0) + doc;
  const outTheDoor = price + salesTax + totalFees - trade;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b bg-gradient-to-r from-green-600 to-green-700 rounded-t-xl text-white flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Pre-Sale Calculator</h2>
            <p className="text-green-100 text-xs">Customer Out-The-Door Cost</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none">&times;</button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-neutral-500">Loading tax rates...</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Sale Price */}
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Sale Price</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <input type="number" value={salePrice} onChange={e => setSalePrice(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border rounded-lg text-lg font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="0.00" autoFocus />
              </div>
            </div>

            {/* County + Out of State */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-600 block mb-1">County (for tax)</label>
                <select value={county} onChange={e => setCounty(e.target.value)}
                  disabled={isOutOfState}
                  className="w-full px-2 py-2 border rounded-lg text-sm disabled:opacity-50">
                  {Object.keys(taxRates).sort().map(c => (
                    <option key={c} value={c}>{c} ({taxRates[c]}%)</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={isOutOfState} onChange={e => setIsOutOfState(e.target.checked)}
                    className="rounded text-green-600" />
                  <span className="text-sm">Out of State (no OH tax)</span>
                </label>
              </div>
            </div>

            {/* Trade-In */}
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Trade-In Value (deducted from taxable amount)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <input type="number" value={tradeIn} onChange={e => setTradeIn(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="0.00" />
              </div>
            </div>

            {/* Doc Fee */}
            <div>
              <label className="text-xs font-medium text-neutral-600 block mb-1">Doc Fee</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <input type="number" value={docFee} onChange={e => setDocFee(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="300.00" />
              </div>
            </div>

            {/* Additional Fees */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-neutral-600">Additional Fees</label>
                <button onClick={addFee} className="text-xs text-green-600 hover:text-green-700 font-medium">+ Add Fee</button>
              </div>
              <div className="space-y-2">
                {fees.map(f => (
                  <div key={f.id} className="flex gap-2 items-center">
                    <input type="text" value={f.name} onChange={e => updateFee(f.id, 'name', e.target.value)}
                      className="flex-1 px-2 py-1.5 border rounded-lg text-xs" placeholder="Fee name" />
                    <div className="relative w-24">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400 text-xs">$</span>
                      <input type="number" value={f.amount} onChange={e => updateFee(f.id, 'amount', e.target.value)}
                        className="w-full pl-5 pr-2 py-1.5 border rounded-lg text-xs font-mono" />
                    </div>
                    <button onClick={() => removeFee(f.id)} className="text-red-400 hover:text-red-600 text-sm">&times;</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Sale Price</span>
                <span className="font-mono">${price.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              {trade > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>- Trade-In</span>
                  <span className="font-mono">-${trade.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span>Taxable Amount</span>
                <span className="font-mono">${taxableAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Sales Tax ({isOutOfState ? 'Out of State' : county + ' ' + taxRate + '%'})</span>
                <span className="font-mono">${salesTax.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Doc Fee</span>
                <span className="font-mono">${doc.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              {fees.filter(f => (parseFloat(f.amount) || 0) > 0).map(f => (
                <div key={f.id} className="flex justify-between text-sm">
                  <span>{f.name || 'Fee'}</span>
                  <span className="font-mono">${(parseFloat(f.amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="border-t border-green-300 my-2"></div>
              <div className="flex justify-between text-lg font-bold text-green-800">
                <span>Out-The-Door Total</span>
                <span className="font-mono">${outTheDoor.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            {/* Reset */}
            <div className="flex justify-between">
              <button onClick={() => { setSalePrice(''); setTradeIn(''); setDocFee(300); setCounty('Lucas'); setIsOutOfState(false); setFees([{id:1,name:'Temp Tag',amount:20},{id:2,name:'Title Fee',amount:22.50},{id:3,name:'Lien Fee',amount:0}]); }}
                className="px-3 py-1.5 text-xs bg-neutral-100 rounded-lg hover:bg-neutral-200">
                Reset
              </button>
              <button onClick={onClose} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreSaleCalculator;
